import { useState, useEffect, useCallback } from 'react';
import { useJwt, useAuthHeaders } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Badge, Button } from '../ds/index.js';

interface RecordingRow {
  id: string;
  mapZoneId: string;
  /** Nom de la salle (absent si l'API est plus ancienne que le front). */
  roomName?: string;
  status: string;
  durationSeconds: number | null;
  startedAt: string;
  endedAt: string | null;
  /** Envoi au Drive : null (jamais demandé) | queued | transcribing | uploading | done | failed */
  exportStatus?: ExportStatus | null;
  exportError?: string | null;
  transcriptKey?: string | null;
}

type ExportStatus = 'queued' | 'transcribing' | 'uploading' | 'done' | 'failed';
const EXPORT_IN_PROGRESS: ExportStatus[] = ['queued', 'transcribing', 'uploading'];
/** Rafraîchissement automatique tant qu'un envoi au Drive est en cours. */
const EXPORT_POLL_MS = 10_000;

function getTenantIdFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.tid as string) ?? '';
  } catch { return ''; }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = (seconds % 60).toString().padStart(2, '0');
  return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${s}` : `${m}:${s}`;
}

const EXPORT_BADGE: Record<ExportStatus, { tone: 'warning' | 'neutral' | 'success' | 'danger' | 'primary'; label: string }> = {
  queued: { tone: 'neutral', label: 'En file' },
  transcribing: { tone: 'primary', label: 'Transcription…' },
  uploading: { tone: 'primary', label: 'Envoi…' },
  done: { tone: 'success', label: 'Envoyé' },
  failed: { tone: 'danger', label: 'Échec' },
};

const EXPORT_ERRORS: Record<string, string> = {
  NOT_READY: 'Cet enregistrement n’est pas encore prêt.',
  IN_PROGRESS: 'Cet enregistrement est déjà en cours d’envoi.',
  DISABLED: 'L’envoi au Drive n’est pas configuré sur cette instance.',
};

const STATUS_BADGE: Record<string, { tone: 'warning' | 'neutral' | 'success' | 'danger'; label: string }> = {
  RECORDING: { tone: 'warning', label: 'En cours' },
  PROCESSING: { tone: 'neutral', label: 'Traitement' },
  READY: { tone: 'success', label: 'Disponible' },
  FAILED: { tone: 'danger', label: 'Échoué' },
};

/**
 * AdminRecordings — liste + téléchargement des enregistrements du tenant.
 *
 * La route backend (recording-download.routes.ts) relaie directement les
 * octets du fichier (PROVISOIRE — voir docs/minio-proxy-provisoire.md,
 * MinIO n'est pas joignable depuis un navigateur) et exige X-Tenant-ID en
 * plus du JWT, deux headers qu'une navigation classique (window.open/
 * <a href>) ne peut pas poser. On fetch() donc la route, on récupère le
 * blob, et on déclenche le téléchargement via un <a download> synthétique.
 */
export function AdminRecordings() {
  const jwt = useJwt();
  const tenantId = getTenantIdFromJwt(jwt);
  const authHeaders = useAuthHeaders(tenantId);

  const [recordings, setRecordings] = useState<RecordingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [driveEnabled, setDriveEnabled] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const load = useCallback((quiet = false) => {
    if (!jwt || !tenantId) return;
    if (!quiet) setLoading(true);
    fetch(`${API_BASE}/media/recordings`, { headers: authHeaders })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => setRecordings(Array.isArray(data) ? data : []))
      .catch(() => { if (!quiet) setError('Impossible de charger les enregistrements.'); })
      .finally(() => { if (!quiet) setLoading(false); });
  }, [jwt, tenantId]);

  useEffect(() => { load(); }, [load]);

  // Envoi au Drive : seulement sur les instances configurées (Copyhouse).
  useEffect(() => {
    if (!jwt || !tenantId) return;
    fetch(`${API_BASE}/media/recordings/export-config`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d: { enabled?: boolean }) => setDriveEnabled(!!d.enabled))
      .catch(() => setDriveEnabled(false));
  }, [jwt, tenantId]);

  // Envoi en cours, ou enregistrement qui se termine (l'envoi au Drive part tout seul quand il est prêt).
  const exporting = recordings.some((r) => r.exportStatus && EXPORT_IN_PROGRESS.includes(r.exportStatus))
    || (driveEnabled && recordings.some((r) => r.status === 'RECORDING' || r.status === 'PROCESSING'));
  useEffect(() => {
    if (!exporting) return;
    const t = setInterval(() => load(true), EXPORT_POLL_MS);
    return () => clearInterval(t);
  }, [exporting, load]);

  async function handleSendToDrive(id: string) {
    if (sendingId) return;
    setSendingId(id);
    try {
      const res = await fetch(`${API_BASE}/media/recordings/${id}/export`, { method: 'POST', headers: authHeaders });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        setError(EXPORT_ERRORS[body.error ?? ''] ?? 'Impossible de lancer l’envoi. Réessaie.');
      }
      load(true);
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    } finally {
      setSendingId(null);
    }
  }

  async function handleTranscript(r: RecordingRow) {
    try {
      const res = await fetch(`${API_BASE}/media/recordings/${r.id}/transcript`, { headers: authHeaders });
      if (!res.ok) { setError('Transcription indisponible.'); return; }
      const blobUrl = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `transcription-${r.id.slice(0, 8)}.txt`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    }
  }

  async function handleDownload(id: string) {
    if (downloadingId) return;
    setDownloadingId(id);
    try {
      const res = await fetch(`${API_BASE}/media/recordings/${id}/download`, { headers: authHeaders });
      if (!res.ok) {
        setError('Impossible de récupérer le fichier. Réessaie.');
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `enregistrement-${id.slice(0, 8)}.mp4`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    } finally {
      setDownloadingId(null);
    }
  }

  const n = recordings.length;

  return (
    <>
      <header className="pl-page-head">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 className="pl-page-title">Enregistrements</h1>
          <p className="pl-page-sub">
            {loading ? 'Chargement…' : `${n} enregistrement${n > 1 ? 's' : ''} · conservés dans l'espace de stockage de ton instance`}
          </p>
          {driveEnabled && (
            <p className="pl-page-sub">Chaque enregistrement part tout seul au Drive avec sa transcription quand il est prêt. « Réessayer » relance un envoi en échec.</p>
          )}
        </div>
        <Button variant="secondary" icon="refresh" onClick={() => load()} disabled={loading}>Actualiser</Button>
      </header>

      {error && (
        <div className="pl-alert" role="alert" style={{ justifyContent: 'space-between' }}>
          <span>{error}</span>
          <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={() => setError(null)} />
        </div>
      )}

      {!loading && (
        <div className="pl-panel" style={{ overflow: 'hidden' }}>
          <table className="pl-table">
            <thead><tr><th>Salle</th><th>Statut</th><th>Démarré</th><th>Durée</th>{driveEnabled && <th>Drive</th>}<th><span className="pl-sr">Actions</span></th></tr></thead>
            <tbody>
              {n === 0 && (
                <tr><td colSpan={driveEnabled ? 6 : 5} className="pl-muted" style={{ textAlign: 'center', padding: 24 }}>
                  Aucun enregistrement. On lance un enregistrement depuis une salle de réunion, bouton « Enregistrer ».
                </td></tr>
              )}
              {recordings.map((r) => {
                const badge = STATUS_BADGE[r.status] ?? { tone: 'neutral' as const, label: r.status };
                const exportBadge = r.exportStatus ? EXPORT_BADGE[r.exportStatus] : null;
                const canSend = driveEnabled && r.status === 'READY' && (!r.exportStatus || r.exportStatus === 'failed');
                return (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 500 }}>{r.roomName ?? <span className="pl-muted" style={{ font: '700 12px/1 var(--font-mono)' }}>{r.mapZoneId.slice(0, 8)}</span>}</td>
                    <td><Badge tone={badge.tone}>{badge.label}</Badge></td>
                    <td className="pl-muted pl-num">{formatDateTime(r.startedAt)}</td>
                    <td className="pl-num" style={{ font: '700 12px/1 var(--font-mono)' }}>{formatDuration(r.durationSeconds)}</td>
                    {driveEnabled && (
                      <td>
                        {exportBadge
                          ? <span title={r.exportStatus === 'failed' ? (r.exportError ?? undefined) : undefined}><Badge tone={exportBadge.tone}>{exportBadge.label}</Badge></span>
                          : <span className="pl-muted">-</span>}
                      </td>
                    )}
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {canSend && (
                        <Button variant="primary" size="sm" icon="upload" onClick={() => { void handleSendToDrive(r.id); }} disabled={sendingId === r.id}>
                          {r.exportStatus === 'failed' ? 'Réessayer' : 'Envoyer au Drive'}
                        </Button>
                      )}
                      {r.transcriptKey && (
                        <Button variant="ghost" size="sm" icon="file-text" onClick={() => { void handleTranscript(r); }}>Transcription</Button>
                      )}
                      {r.status === 'READY' && (
                        <Button variant="secondary" size="sm" icon="download" onClick={() => { void handleDownload(r.id); }} disabled={downloadingId === r.id}>
                          {downloadingId === r.id ? 'Téléchargement…' : 'Télécharger'}
                        </Button>
                      )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
